/**
 * Every amount in this codebase is an integer number of paise. Rupees exist
 * only in form input and rendered output — never in storage or arithmetic.
 */

const inrWhole = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 0,
});

const inrWithPaise = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `4200000` -> `"₹42,000"`. Indian digit grouping, paise shown only if present. */
export function formatINR(paise: number): string {
  const negative = paise < 0;
  const abs = Math.abs(paise);
  const rupees = abs / 100;
  const body = abs % 100 === 0 ? inrWhole.format(rupees) : inrWithPaise.format(rupees);
  return `${negative ? "-" : ""}₹${body}`;
}

/** `1800000` -> `"₹18k"`. For rate ranges, where precision is noise. */
export function formatCompactINR(paise: number): string {
  const rupees = Math.abs(paise) / 100;
  const sign = paise < 0 ? "-" : "";
  if (rupees >= 10_000_000) return `${sign}₹${trim(rupees / 10_000_000)}Cr`;
  if (rupees >= 100_000) return `${sign}₹${trim(rupees / 100_000)}L`;
  if (rupees >= 1_000) return `${sign}₹${trim(rupees / 1_000)}k`;
  return `${sign}₹${trim(rupees)}`;
}

function trim(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** Parses rupee input from a form. Accepts "28,000", "28000.50", "₹28k" is rejected. */
export function parseRupeesToPaise(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return null;
  const cleaned = String(input).replace(/[₹,\s]/g, "");
  if (cleaned === "" || !/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export function paiseToRupeeString(paise: number): string {
  return paise % 100 === 0 ? String(paise / 100) : (paise / 100).toFixed(2);
}

/**
 * Splits `total` into `weights.length` parts proportional to the weights, with
 * every remainder paisa pushed onto the last part so the parts always sum to
 * exactly `total`. This is why the ledger balances.
 */
export function splitProportionally(total: number, weights: number[]): number[] {
  const weightSum = weights.reduce((a, b) => a + b, 0);
  if (weightSum <= 0) throw new Error("splitProportionally: weights must sum above zero");

  const parts = weights.map((w) => Math.floor((total * w) / weightSum));
  const allocated = parts.reduce((a, b) => a + b, 0);
  parts[parts.length - 1] += total - allocated;
  return parts;
}

/** Basis points of an amount, rounded half-up. 400 bps of 100000 -> 4000. */
export function applyBps(amount: number, bps: number): number {
  return Math.round((amount * bps) / 10_000);
}
